import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  inject,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { Subject, of, forkJoin } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, catchError, finalize, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { NetworkMetadataService } from '@olv/ui-shared/services/network-metadata.service';
import { RiskLevelEnum } from '@olv/ui-shared/enums/task.enums';
import { PagedResultDto } from '@olv/ui-shared/models/paged-result.dto';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import {
  WalletBalances,
  WalletTypeEnum,
  WalletStatusEnum,
  WalletLifecycleStateEnum,
  WalletDto,
  WalletFilterParams,
  CreateMerchantWalletDto,
  CurrencyBalances,
} from '@olv/ui-shared/models/wallet.model';
import { MerchantRiskGroup } from '@olv/ui-shared/models/merchant.model';
import { RiskGroupShortCodeEnum } from '@olv/ui-shared/enums/risk-group-short-code.enum';
import {
  ApiWalletViewModel,
  OneTimeWalletViewModel,
  WalletViewModel,
} from '@olv/ui-shared/models/wallet-view-model.model';
import { WalletService } from '@olv/ui-shared/services/wallet.service';
import { PillSearchComponent, SearchState } from '@olv/ui-shared/ui-shared/pill-search/pill-search.component';
import { WalletCardComponent, walletsByNetwork } from '@olv/ui-shared/ui-shared/wallet-card/wallet-card.component';
import { CreateWalletFormComponent } from '@olv/ui-shared/ui-shared/create-wallet-form/create-wallet-form.component';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { formatDateStandard } from '@olv/ui-shared/utils/dateFormat.utils';
import { AppliedFilter, FilterService } from '@olv/ui-shared/services/filter.service';
import { WalletNavigationState, WalletListNavState } from '@olv/ui-shared/models/wallet-navigation-state.model';
import { FormatService } from '../../../services/format.service';
import { enumToFilterFields } from '@olv/ui-shared/utils/enum-to-filter.util';
import { shortenAddress } from '@olv/ui-shared/utils/shortenAddress.utils';
import { LoadingSpinnerComponent } from '@olv/ui-shared/ui-shared/loading-spinner/loading-spinner.component';
import { StateBadgeComponent } from '@olv/ui-shared/ui-shared/state-badge/state-badge.component';
import { FiltersDialogComponent } from '@olv/ui-shared/ui-shared/filters-dialog/filters.component';
import { ScrollRowComponent } from '@olv/ui-shared/ui-shared/scroll-row/scroll-row.component';
import { ShowMoreComponent } from '@olv/ui-shared/ui-shared/show-more/show-more.component';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';

export interface PooledWalletGroup {
  code: string;
  shortCode: RiskGroupShortCodeEnum | null;
  label: string;
  wallets: WalletViewModel[];
}

export type WalletTab = 'pooled' | 'dedicated' | 'one-time' | 'api';

@Component({
  selector: 'app-merchant-wallet-management',
  standalone: true,
  imports: [
    CommonModule,
    WalletCardComponent,
    PillSearchComponent,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    CreateWalletFormComponent,
    MatTooltipModule,
    MatIconModule,
    HasPermissionDirective,
    LoadingSpinnerComponent,
    StateBadgeComponent,
    ScrollRowComponent,
    ShowMoreComponent,
    ButtonComponent,
  ],
  templateUrl: './merchant-wallet-management.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./merchant-wallet-management.component.scss'],
})
export class MerchantWalletManagementComponent implements OnInit, OnDestroy, OnChanges {
  private walletService = inject(WalletService);
  private fb = inject(FormBuilder);
  private router = inject(Router);

  protected readonly walletsByNetwork = walletsByNetwork;
  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;
  protected readonly formatDateStandard = formatDateStandard;
  readonly shortenAddress = shortenAddress;

  // Expose enums to the template
  WalletTypeEnum = WalletTypeEnum;
  WalletStatusEnum = WalletStatusEnum;

  @Input() merchantId = '';
  @Input() totalWallets = 0;
  @Input() wallets: WalletDto[] = [];
  @Input() initialApiWallets: WalletDto[] = [];
  @Input() isFirstLoad = false;
  /** True while the parent is still fetching the wallets passed in through `wallets` and `initialApiWallets`. */
  @Input() isLoadingInitialWallets = false;
  @Input() isDisableFilters = false;
  @Input() oneTimeWalletsCount = 0;
  @Input() apiWalletsCount = 0;
  @Input() hasMoreWallets = false;
  @Input() hasMoreApiWallets = false;
  @Input() riskGroups: MerchantRiskGroup[] = [];

  @Output() walletCreated = new EventEmitter<string>();
  @ViewChild(CreateWalletFormComponent) createWalletFormComponent!: CreateWalletFormComponent;

  // Search and filter
  searchText = '';
  get filters() {
    return [
      { name: 'Risk Level', filterFields: enumToFilterFields(RiskLevelEnum) },
      { name: 'Blockchain', filterFields: this.metadata.filterFields() },
      { name: 'Status', filterFields: enumToFilterFields(WalletStatusEnum) },
      ...(this.activeTab === 'pooled'
        ? [{ name: 'Lifecycle State', filterFields: enumToFilterFields(WalletLifecycleStateEnum) }]
        : []),
      ...(this.activeTab === 'api'
        ? [
            {
              name: 'Wallet Type',
              filterFields: [
                { name: 'One-Time', value: WalletTypeEnum.OnetimeUse },
                { name: 'Dedicated', value: WalletTypeEnum.Dedicated },
              ],
            },
          ]
        : []),
      {
        name: 'Balance',
        filterFields: [
          { name: 'Balance Above Zero (>0)', value: 1 },
          { name: 'Zero Balance (=0)', value: 0 },
        ],
      },
    ];
  }

  readonly DEFAULT_WALLET_STATUS: WalletStatusEnum[] = [WalletStatusEnum.Active];

  // Dropdown options
  sortByOptions: string[] = ['Newest', 'Oldest', 'Highest Balance', 'Lowest Balance'];
  statusOptions: string[] = ['All', 'Active', 'Inactive', 'Pending'];
  actionOptions: string[] = ['All', 'Required', 'Not Required'];

  // Selected values
  filterMap: Partial<WalletFilterParams> = {};
  filtersApplied = false;

  // Track which dropdown is open
  openDropdown: string | null = null;

  // Wallet data
  poolWallets: WalletViewModel[] = [];
  unsupportedFundsWallets: WalletViewModel[] = [];
  dedicatedWallets: WalletViewModel[] = [];
  oneTimeWallets: OneTimeWalletViewModel[] = [];
  apiWallets: ApiWalletViewModel[] = [];
  pooledGroups: PooledWalletGroup[] = [];

  // Loading and error states
  isLoading = false;
  isCreatingWallet = false;
  isMoreLoading = false;
  error: string | null = null;

  // Pagination
  pageSize = 10;
  pageIndex = 0;

  // Wallet type counts
  poolWalletsCount = 0;
  dedicatedWalletsCount = 0;
  isMoreApiLoading = false;
  isApiLoading = false;
  apiError: string | null = null;
  apiPageIndex = 0;
  private apiLoadToken = 0;

  // Manually created wallets total from the component's own reloads; null until the first
  // self-managed load so the parent-provided totalWallets input is not overwritten
  private manualWalletsTotal: number | null = null;

  // UI state
  activeTab: WalletTab = 'pooled';
  private selfManagedWallets = false;
  collapsedGroups = new Set<string>();
  selectedWalletId: string | null = null;
  isCreateWalletFormVisible = false;

  readonly LEGACY_GROUP_CODE = '__legacy';
  readonly UNSUPPORTED_FUNDS_GROUP_CODE = '__unsupported-funds';

  // Form for creating wallets
  createWalletForm: FormGroup;

  // RxJS subjects
  private destroy$ = new Subject<void>();
  private searchTerms = new Subject<string>();

  networkName(networkType: number): string {
    if (this.metadata.error()) return '-';
    return this.metadata.network(networkType)?.name ?? '';
  }

  private filterService = inject(FilterService);
  private dialog = inject(MatDialog);
  private formatService = inject(FormatService);
  protected readonly metadata = inject(NetworkMetadataService);

  constructor() {
    this.metadata.load();
    this.createWalletForm = this.fb.group({
      networkType: [null, Validators.required],
      walletType: [WalletTypeEnum.Pooled, Validators.required],
    });
  }

  ngOnInit(): void {
    const restored = this.restoreListState();
    if (!restored) {
      this.applyDefaultFilter();
    }
    // Setup search debounce
    this.searchTerms.pipe(takeUntil(this.destroy$), debounceTime(300), distinctUntilChanged()).subscribe((term) => {
      this.searchText = term;
      this.pageIndex = 0; // Reset to first page on new search
      this.loadWallets();
    });
    this.groupWalletsByType(this.wallets);

    // Initial load
    if (this.merchantId) {
      console.log('Merchant Wallet Management: Loading wallets for merchant ID:', this.merchantId);
    } else {
      console.warn('Merchant Wallet Management: No merchant ID provided');
      this.error = 'No merchant ID provided. Please ensure you are viewing a valid merchant.';
    }
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['wallets'] && !this.selfManagedWallets) {
      this.groupWalletsByType(this.wallets);
    }
    if (changes['initialApiWallets'] && !this.selfManagedWallets) {
      this.apiWallets = this.initialApiWallets.map((wallet) => this.transformToApiWalletViewModel(wallet));
    }
    if (changes['riskGroups'] && !changes['wallets']) {
      this.buildPooledGroups();
    }
    // Check if merchantId has changed
    if (changes['merchantId']) {
      const currentValue = changes['merchantId'].currentValue;
      console.log('Merchant ID changed:', currentValue);

      // Only reload if we have a valid merchantId and it's not the first change
      // (first change is handled by ngOnInit)
      if (currentValue && !changes['merchantId'].firstChange) {
        this.loadWalletsWithFilter({
          ...this.filterMap,
          skipCount: 0,
          filter: this.searchText?.trim() || undefined,
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  private applyDefaultFilter(): void {
    this.filterMap = {
      status: [...this.DEFAULT_WALLET_STATUS],
    };

    this.filtersApplied = true;
    this.searchText = '';
  }

  private restoreListState(): boolean {
    const navState = window.history.state;
    const listState: WalletListNavState | undefined = navState?.fromWalletDetails ? navState.listState : undefined;
    if (!listState) {
      return false;
    }

    this.activeTab = listState.activeTab ?? this.activeTab;
    this.filterMap = listState.filterMap ?? {};
    this.filtersApplied = !!listState.filtersApplied;
    this.searchText = listState.searchText ?? '';

    if (this.merchantId) {
      this.selfManagedWallets = true;
      this.loadWalletsWithFilter({
        ...this.filterMap,
        skipCount: 0,
        filter: this.searchText?.trim() || undefined,
      });
    }

    return true;
  }

  /**
   * Load wallets from the service with current filters
   */
  loadWallets(): void {
    if (!this.merchantId) {
      console.error('Merchant Wallet Management: merchantId is empty or null');
      this.error = 'No merchant ID provided. Please ensure you are viewing a valid merchant.';
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.error = null;

    // Convert UI filter selections to API parameters
    const params: WalletFilterParams = {
      merchantId: this.merchantId,
      skipCount: this.pageSize ? this.pageIndex * this.pageSize : 0,
      filter: this.searchText || undefined,
    };
    this.walletService
      .getMerchantWallets(this.merchantId, params)
      .pipe(
        takeUntil(this.destroy$),
        tap((result) => {
          console.log(
            `Merchant Wallet Management: Received ${result.items.length} wallets for merchant ID ${this.merchantId}`,
          );
          if (result.items.length === 0) {
            console.log('Merchant Wallet Management: No wallets returned');
          }
        }),
        finalize(() => (this.isLoading = false)),
        catchError((error) => {
          console.error('Merchant Wallet Management: Error loading wallets:', error);
          this.error = `Failed to load wallets: ${error.message}`;
          return of({ items: [], totalCount: 0 });
        }),
      )
      .subscribe((result: PagedResultDto<WalletDto>) => {
        this.manualWalletsTotal = result.totalCount;
        this.updateTotalWallets();
        console.log(`Merchant Wallet Management: Total wallets count: ${this.totalWallets}`);

        // Group wallets by type and transform to view models
        this.groupWalletsByType(result.items);
      });

    this.loadApiWallets({
      ...this.filterMap,
      skipCount: 0,
      filter: this.searchText?.trim() || undefined,
    });
  }

  private updateTotalWallets(): void {
    if (this.manualWalletsTotal !== null) {
      this.totalWallets = this.manualWalletsTotal + this.apiWalletsCount;
    }
  }

  /**
   * Apply filters to the wallets
   */
  onFiltersApplied(values: AppliedFilter[]) {
    this.filtersApplied = values.length > 0;

    this.filterMap = this.filterService.applySelectedFilters(values, {});

    const filter: WalletFilterParams = {
      ...this.filterMap,
      skipCount: 0,
      filter: this.searchText?.trim() || undefined,
    };

    this.loadWalletsWithFilter(filter);
  }

  loadMoreWallets(): void {
    if (!this.isLoading && !this.isMoreLoading && this.hasMoreWallets) {
      const nextPageIndex = this.pageIndex + 1;
      const filter: WalletFilterParams = {
        ...this.filterMap,
        skipCount: nextPageIndex * this.pageSize,
        walletType: [WalletTypeEnum.OnetimeUse],
        maxResultCount: this.pageSize,
        filter: this.searchText?.trim() || undefined,
      };
      this.isMoreLoading = true;
      this.walletService.getMerchantWallets(this.merchantId, filter).subscribe({
        next: (result: PagedResultDto<WalletDto>) => {
          this.pageIndex = nextPageIndex;
          const newWallets = result.items.map((wallet: WalletDto) => this.transformToOneTimeWalletViewModel(wallet));
          this.oneTimeWallets = [...this.oneTimeWallets, ...newWallets];
          this.oneTimeWalletsCount = result.totalCount;
          this.hasMoreWallets = this.oneTimeWallets.length < result.totalCount;
          this.isMoreLoading = false;
        },
        error: () => (this.isMoreLoading = false),
      });
    }
  }
  /**
   * Load wallets filtering options
   */
  loadWalletsWithFilter(filter: WalletFilterParams): void {
    if (!this.merchantId) {
      console.error('Merchant Wallet Management: merchantId is empty or null');
      this.error = 'No merchant ID provided. Please ensure you are viewing a valid merchant.';
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    this.error = null;
    this.pageIndex = 0;

    filter.merchantId = this.merchantId;

    // The types served by the pooled + dedicated call. UnsupportedFunds belongs here rather than with the
    // paginated one-time call: it renders in the Pool Wallets section, and there is at most one per network.
    const poolSectionTypes = [WalletTypeEnum.Pooled, WalletTypeEnum.Dedicated, WalletTypeEnum.UnsupportedFunds];

    const walletTypes = filter.walletType;
    const includePoolDedicated = !walletTypes || walletTypes.some((type: number) => poolSectionTypes.includes(type));
    const includeOneTime = !walletTypes || walletTypes.includes(WalletTypeEnum.OnetimeUse);

    const poolDedicatedTypes = walletTypes
      ? walletTypes.filter((type: number) => poolSectionTypes.includes(type))
      : poolSectionTypes;

    const poolAndDedicated$ = includePoolDedicated
      ? this.walletService.getMerchantWallets(this.merchantId, { ...filter, walletType: poolDedicatedTypes }).pipe(
          catchError(() => {
            return of({ items: [], totalCount: 0 });
          }),
        )
      : of({ items: [], totalCount: 0 });

    const oneTime$ = includeOneTime
      ? this.walletService
          .getMerchantWallets(this.merchantId, {
            ...filter,
            walletType: [WalletTypeEnum.OnetimeUse],
            maxResultCount: 10,
            skipCount: 0,
          })
          .pipe(
            catchError(() => {
              return of({ items: [], totalCount: 0 });
            }),
          )
      : of({ items: [], totalCount: 0 });

    forkJoin({
      poolAndDedicated: poolAndDedicated$,
      oneTime: oneTime$,
    })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe(({ poolAndDedicated, oneTime }) => {
        this.manualWalletsTotal = poolAndDedicated.totalCount + oneTime.totalCount;
        this.updateTotalWallets();
        this.hasMoreWallets = oneTime.items.length < oneTime.totalCount;
        this.groupWalletsByType([...poolAndDedicated.items, ...oneTime.items]);
        this.oneTimeWalletsCount = oneTime.totalCount;
      });

    this.loadApiWallets(filter);
  }

  private loadApiWallets(filter: WalletFilterParams): void {
    this.apiPageIndex = 0;
    this.isApiLoading = true;
    this.isMoreApiLoading = false;
    this.apiError = null;

    const requestToken = ++this.apiLoadToken;
    this.walletService
      .getMerchantApiWallets(this.merchantId, { ...filter, maxResultCount: this.pageSize, skipCount: 0 })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          if (requestToken === this.apiLoadToken) {
            this.isApiLoading = false;
          }
        }),
      )
      .subscribe({
        next: (result: PagedResultDto<WalletDto>) => {
          if (requestToken !== this.apiLoadToken) {
            return;
          }
          this.apiWallets = result.items.map((wallet) => this.transformToApiWalletViewModel(wallet));
          this.apiWalletsCount = result.totalCount;
          this.hasMoreApiWallets = this.apiWallets.length < result.totalCount;
          this.updateTotalWallets();
        },
        error: () => {
          if (requestToken !== this.apiLoadToken) {
            return;
          }
          this.apiWallets = [];
          this.apiWalletsCount = 0;
          this.hasMoreApiWallets = false;
          this.apiError = 'Failed to load API wallets.';
          this.updateTotalWallets();
        },
      });
  }

  loadMoreApiWallets(): void {
    if (this.isApiLoading || this.isMoreApiLoading || !this.hasMoreApiWallets) {
      return;
    }
    // Pin this pagination request to the current initial-load generation. If a filter reload starts before it
    // returns, the response is stale and must neither append rows nor touch loading state for the newer result set.
    const requestToken = this.apiLoadToken;
    const nextPageIndex = this.apiPageIndex + 1;
    this.isMoreApiLoading = true;
    this.walletService
      .getMerchantApiWallets(this.merchantId, {
        ...this.filterMap,
        skipCount: nextPageIndex * this.pageSize,
        maxResultCount: this.pageSize,
        filter: this.searchText?.trim() || undefined,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          if (requestToken === this.apiLoadToken) {
            this.isMoreApiLoading = false;
          }
        }),
      )
      .subscribe({
        next: (result: PagedResultDto<WalletDto>) => {
          if (requestToken !== this.apiLoadToken) {
            return;
          }
          this.apiPageIndex = nextPageIndex;
          const newWallets = result.items.map((wallet) => this.transformToApiWalletViewModel(wallet));
          this.apiWallets = [...this.apiWallets, ...newWallets];
          this.apiWalletsCount = result.totalCount;
          this.hasMoreApiWallets = this.apiWallets.length < result.totalCount;
        },
        error: () => {},
      });
  }
  /**
   * Load wallet with walletID
   */
  loadWalletWithID(): void {
    console.log('Loading wallet with ID:', this.selectedWalletId);
    if (!this.selectedWalletId) {
      return;
    }
    this.walletService.getMerchantWallet(this.selectedWalletId).subscribe({
      next: (wallet) => {
        this.groupWalletsByType([wallet]);
      },
      error: (err) => {
        console.error('Failed to load wallet:', err);
      },
    });
  }

  /**
   * Group wallets by their type and transform to view models
   */
  private groupWalletsByType(wallets: WalletDto[]): void {
    // Reset arrays
    this.poolWallets = [];
    this.unsupportedFundsWallets = [];
    this.dedicatedWallets = [];
    this.oneTimeWallets = [];

    // Group by wallet type
    wallets.forEach((wallet) => {
      // Transform to view model based on wallet type
      switch (wallet.walletType) {
        case WalletTypeEnum.Pooled:
          this.poolWallets.push(this.transformToWalletViewModel(wallet));
          break;
        case WalletTypeEnum.Dedicated:
          this.dedicatedWallets.push(this.transformToWalletViewModel(wallet));
          break;
        case WalletTypeEnum.OnetimeUse:
          this.oneTimeWallets.push(this.transformToOneTimeWalletViewModel(wallet));
          break;
        case WalletTypeEnum.UnsupportedFunds:
          this.unsupportedFundsWallets.push(this.transformToWalletViewModel(wallet));
          break;
      }
    });

    // Update counts — the Unsupported Deposit Funds wallet renders in the Pool Wallets section, so it counts there.
    this.poolWalletsCount = this.poolWallets.length + this.unsupportedFundsWallets.length;
    this.dedicatedWalletsCount = this.dedicatedWallets.length;

    this.buildPooledGroups();
  }

  private buildPooledGroups(): void {
    const groups: PooledWalletGroup[] = (this.riskGroups || []).map((riskGroup) => ({
      code: String(riskGroup.shortCode),
      shortCode: riskGroup.shortCode,
      label: riskGroup.label,
      wallets: this.poolWallets.filter((wallet) => wallet.riskGroupShortCode === riskGroup.shortCode),
    }));

    const knownShortCodes = new Set(groups.map((group) => group.shortCode));
    const legacyWallets = this.poolWallets.filter(
      (wallet) => wallet.riskGroupShortCode == null || !knownShortCodes.has(wallet.riskGroupShortCode),
    );

    // Between the risk-managed groups and Legacy: this wallet is outside the risk tiers, but it is not a legacy
    // pooled wallet either — a legacy wallet still funds cross-risk sourcing, and this one never does.
    if (this.unsupportedFundsWallets.length > 0) {
      groups.push({
        code: this.UNSUPPORTED_FUNDS_GROUP_CODE,
        shortCode: null,
        label: 'Unsupported Deposit Funds',
        wallets: this.unsupportedFundsWallets,
      });
    }

    if (legacyWallets.length > 0) {
      groups.push({ code: this.LEGACY_GROUP_CODE, shortCode: null, label: 'Legacy Wallets', wallets: legacyWallets });
    }

    this.pooledGroups = groups.filter((group) => group.wallets.length > 0);
  }

  toggleGroup(code: string): void {
    if (this.collapsedGroups.has(code)) {
      this.collapsedGroups.delete(code);
    } else {
      this.collapsedGroups.add(code);
    }
  }

  isGroupCollapsed(code: string): boolean {
    return this.collapsedGroups.has(code);
  }

  selectTab(tab: WalletTab): void {
    this.activeTab = tab;
  }

  private static readonly RISK_GROUP_DOT_CLASSES: Record<RiskGroupShortCodeEnum, string> = {
    [RiskGroupShortCodeEnum.LowExchange]: 'dot-low',
    [RiskGroupShortCodeEnum.Low]: 'dot-low',
    [RiskGroupShortCodeEnum.Medium]: 'dot-medium',
    [RiskGroupShortCodeEnum.High]: 'dot-high',
    [RiskGroupShortCodeEnum.Severe]: 'dot-severe',
    [RiskGroupShortCodeEnum.NotAssessed]: 'dot-default',
  };

  getGroupDotClass(group: PooledWalletGroup): string {
    if (group.code === this.UNSUPPORTED_FUNDS_GROUP_CODE) {
      return 'dot-unsupported';
    }
    if (group.shortCode == null) {
      return 'dot-default';
    }
    return MerchantWalletManagementComponent.RISK_GROUP_DOT_CLASSES[group.shortCode] ?? 'dot-default';
  }

  /**
   * Transform wallet DTO to view model for display
   */
  private transformToWalletViewModel(wallet: WalletDto): WalletViewModel {

    // Determine risk level from backend data
    let risk: string | undefined;
    if (wallet.status === WalletStatusEnum.Pending) {
      risk = 'pending';
    } else if (wallet.riskLevel !== undefined) {
      switch (wallet.riskLevel) {
        case RiskLevelEnum.Unsupported:
          risk = 'unsupported';
          break;
        case RiskLevelEnum.Low:
          risk = 'low';
          break;
        case RiskLevelEnum.Medium:
          risk = 'medium';
          break;
        case RiskLevelEnum.High:
          risk = 'high';
          break;
        case RiskLevelEnum.Unknown:
          risk = 'no-risk';
          break;
        case RiskLevelEnum.NotAssessed:
        default:
          risk = 'not-assessed';
      }
    } else {
      risk = 'not-assessed';
    }

    return {
      id: wallet.id,
      name: wallet.walletName
        ? wallet.walletName
        : `${this.metadata.network(wallet.networkType)?.code ?? ''} - ${
            risk?.charAt(0).toUpperCase() + risk?.slice(1)
          }`,
      address: wallet.address,
      networkType: wallet.networkType,
      isExternalWallet: wallet.isExternalWallet,
      balance: wallet.balance,
      risk,
      pending: wallet.status === WalletStatusEnum.Pending,
      nonce: wallet.nonce,
      isActive: wallet.isActive,
      isSweepingEnabled: wallet.isSweepingEnabled,
      riskGroupShortCode: wallet.riskGroupShortCode,
      lifecycleState: wallet.lifecycleState,
      isServing: wallet.isServing,
    };
  }

  /**
   * Transform wallet DTO to one-time wallet view model
   */
  private transformToOneTimeWalletViewModel(wallet: WalletDto): OneTimeWalletViewModel {
    // Get risk level from backend data
    let riskLevel = 'Not Assessed';
    if (wallet.riskLevel !== undefined) {
      switch (wallet.riskLevel) {
        case RiskLevelEnum.Unsupported:
          riskLevel = 'Unsupported';
          break;
        case RiskLevelEnum.NotAssessed:
          riskLevel = 'NotAssessed';
          break;
        case RiskLevelEnum.Unknown:
          riskLevel = 'Unknown';
          break;
        case RiskLevelEnum.Low:
          riskLevel = 'Low';
          break;
        case RiskLevelEnum.Medium:
          riskLevel = 'Medium';
          break;
        case RiskLevelEnum.High:
          riskLevel = 'High';
          break;
      }
    }

    return {
      id: wallet.id,
      address: wallet.address,
      networkType: wallet.networkType,
      name: wallet.walletName || '-',
      interactionDate: this.formatDateStandard(wallet.updatedAt),
      withdrawalType: wallet.withdrawalType!,
      riskLevel: riskLevel,
      status: this.getStatusString(wallet.status),
      balance: wallet.balance,
      isActive: wallet.isActive,
    };
  }

  /**
   * Transform wallet DTO to API wallet view model
   */
  private transformToApiWalletViewModel(wallet: WalletDto): ApiWalletViewModel {
    return {
      ...this.transformToOneTimeWalletViewModel(wallet),
      walletTypeLabel: wallet.walletType === WalletTypeEnum.Dedicated ? 'Dedicated' : 'One-Time',
    };
  }

  /**
   * Convert status enum to display string
   */
  private getStatusString(status: WalletStatusEnum): string {
    switch (status) {
      case WalletStatusEnum.Active:
        return 'Active';
      case WalletStatusEnum.Inactive:
        return 'Inactive';
      case WalletStatusEnum.Pending:
        return 'Pending';
      default:
        return 'Unknown';
    }
  }

  /**
   * Handle search input changes
   */
  onSearchChange(searchState: SearchState): void {
    this.searchText = searchState.value;
    const filter: WalletFilterParams = {
      ...this.filterMap,
      skipCount: 0,
      filter: searchState.value?.trim() || undefined,
    };
    this.loadWalletsWithFilter(filter);
  }

  /**
   * Show the create wallet form
   */
  openCreateWalletForm(): void {
    this.isCreateWalletFormVisible = true;
  }

  /**
   * Hide the create wallet form
   */
  closeCreateWalletForm(): void {
    this.isCreateWalletFormVisible = false;
    this.createWalletForm.reset({
      networkType: null,
      walletType: WalletTypeEnum.Pooled,
    });
  }

  /**
   * Submit the create wallet form
   */
  createWallet(formData: any): void {
    console.log('Received form data in parent:', formData);
    console.log('Form data type:', typeof formData);
    console.log('Form data keys:', Object.keys(formData));

    // Store the wallet name for later use in UI
    const walletName = formData.name;

    const createDto: CreateMerchantWalletDto = {
      merchantId: this.merchantId,
      addressFamily: formData.addressFamily,
      walletType: formData.walletType,
      name: walletName, // Add the name to the DTO
    };

    console.log('Creating wallet with data:', createDto);

    this.isCreatingWallet = true;
    this.walletService
      .createMerchantWallet(createDto)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isCreatingWallet = false;
        }),
        catchError((error) => {
          console.error('Error creating wallet:', error);

          // Handle backend validation errors
          if (error.status === 400 && error.error?.errors) {
            this.createWalletFormComponent?.setBackendErrors(error.error.errors);
          } else {
            this.error = `Failed to create wallet: ${error.message}`;
            this.closeCreateWalletForm();
          }
          return of(null);
        }),
      )
      .subscribe((result) => {
        if (result) {
          console.log('Wallet created successfully, wallet ID:', result.id);

          // Close form and emit event
          this.closeCreateWalletForm();

          // Emit event to notify parent component that a wallet was created
          // This allows the parent to refresh all child components
          this.walletCreated.emit(result.id);

          this.applyDefaultFilter();
        }
      });
  }

  getWalletCurrencies(wallet: WalletViewModel): { name: string; amount: number; displayPrecision: number }[] {
    if (!wallet?.balance || !wallet.balance.currencyBalances || wallet.balance.currencyBalances.length === 0) {
      return [];
    }

    return wallet.balance.currencyBalances.map((balance) => ({
      name: balance.currencySymbol,
      amount: balance.availableAmountInCurrency,
      displayPrecision: balance.currencyDisplayPrecision,
    }));
  }

  toggleFilters(): void {
    const dialogRef = this.dialog.open(FiltersDialogComponent, {
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: {
        filters: this.filters,
        preselectedValues: this.getAppliedFiltersAsArray(),
        isDisableFilters: this.isDisabled,
      },
    });

    const sub = dialogRef.componentInstance.resetRequest.subscribe((result) => {
      this.onFiltersApplied(result);
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.onFiltersApplied(result);
      }

      sub.unsubscribe();
    });
  }
  clearFilters(): void {
    this.filterMap = {};
    this.filtersApplied = false;
    this.loadWalletsWithFilter({
      skipCount: 0,
    });
  }
  onFilterReset(): void {
    this.filterMap = {};
    this.filtersApplied = false;
    this.loadWalletsWithFilter({
      skipCount: 0,
    });
  }

  getAppliedFiltersAsArray() {
    return this.filterService.getAppliedFilters(this.filterMap);
  }

  openWalletDetail(wallet: WalletViewModel | OneTimeWalletViewModel, walletType: string): void {
    this.selectedWalletId = wallet.id;
    const state: WalletNavigationState = {
      wallet: wallet,
      type: 'merchant',
      returnUrl: this.router.url,
      walletType: walletType,
      listState: {
        activeTab: this.activeTab,
        filterMap: this.filterMap,
        filtersApplied: this.filtersApplied,
        searchText: this.searchText,
      },
    };

    this.router.navigate(['/wallet-detail', wallet.id], { state });
  }
  // Available, not total: a wallet holds the gas funding left over from its last sweep, and showing that as a
  // balance reads as money the merchant has. It is the figure the sweep dialog and the wallet header already use.
  getTokenBalance(wallet: OneTimeWalletViewModel): string {
    if (!this.hasKnownCurrencies(wallet)) return '-';
    const nativeCurrencyId = this.metadata.network(wallet.networkType)?.nativeCurrencyId;
    const tokens = (wallet?.balance?.currencyBalances ?? []).filter((b) => b.currencyId !== nativeCurrencyId);
    if (!tokens.length) return '0';

    return tokens
      .map(
        (b) =>
          `${this.formatService.formatCoinCurrency(b.availableAmountInCurrency, b.currencyDisplayPrecision)} ${
            this.metadata.currency(b.currencyId)?.symbol
          }`,
      )
      .join(', ');
  }
  getNativeBalance(wallet: OneTimeWalletViewModel): string {
    if (!this.hasKnownCurrencies(wallet)) return '-';
    if (!wallet?.balance?.currencyBalances) return '0';

    const nativeCurrencyId = this.metadata.network(wallet.networkType)?.nativeCurrencyId;
    const nativeBalance = wallet.balance.currencyBalances.find(
      (b: CurrencyBalances) => b.currencyId === nativeCurrencyId,
    );

    return this.formatService.formatCoinCurrency(
      nativeBalance?.availableAmountInCurrency ?? 0,
      nativeBalance?.currencyDisplayPrecision,
    );
  }
  /** Own fetches, plus the parent's initial fetch until this component starts loading its own lists. */
  get isBusy(): boolean {
    return this.isLoading || (this.isLoadingInitialWallets && !this.selfManagedWallets);
  }

  private hasKnownCurrencies(wallet: OneTimeWalletViewModel): boolean {
    if (this.metadata.error()) return false;
    const network = this.metadata.network(wallet?.networkType);
    if (!network) return false;
    return (wallet?.balance?.currencyBalances ?? []).every(
      (b) => b.currencyId === network.nativeCurrencyId || !!this.metadata.currency(b.currencyId),
    );
  }
  get isDisabled(): boolean {
    if (this.isFirstLoad) {
      return this.isBusy;
    }

    return this.isDisableFilters;
  }
}
